import { Section } from '@/components/common/Section';
import { Heading } from '@/components/typography';
import { ALL_MENU_QUERY, MenuLocationEnum } from '@/data/allMenuQuery';
import {
  FOOTER_DATA_QUERY,
  FOOTER_DATA_QUERY_EN,
  FooterMenuLocationEnum,
} from '@/data/footerDataQuery';
import { PRIVACY_POLICY_PAGE_QUERY } from '@/data/privacyPolicyPageQuery';
import { withLayout } from '@/layout/Layout';
import { LayoutProps } from '@/layout/Layout/Layout.props';
import { PageProps } from '@/types/types';
import { getApolloClient } from '@/utils/apollo-client';
import { flatListToHierarchical } from '@/utils/flatListToHierarchical';
import { footerDataTransformer } from '@/utils/footerDataTransformer';
import { transformMenus } from '@/utils/transformMenus';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { GetStaticProps } from 'next/types';
import { useEffect, useState } from 'react';
import { PageHead } from '@/components/common/PageHead';

const PrivacyPolicy = ({ data }: PageProps & LayoutProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const { title, seo } = data?.pageBy?.translation ?? {};
  const { content } = data?.pageBy?.translation ?? {};
  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
      <PageHead
        title={seo?.title || title}
        seo={seo || {}}
        canonicalUrl={seo?.canonicalUrl}
        breadcrumbs={seo?.breadcrumbs}
      />
      {isMounted && (
        <>
          <Section breadcrumbs={seo?.breadcrumbs} lazy={false}>
            {title && (
              <Heading
                tag="h1"
                withDivider
                content={title}
                className="mb-20 text-center text-h3Mobile md:mb-25 md:max-w-[90%] md:text-h3"
              />
            )}
            {content && (
              <div
                className="prose-sm w-full text-left text-textPrimary sm:prose prose-headings:mt-0 prose-h3:!mt-4 prose-h3:text-center prose-h3:font-normal prose-p:!mt-0
              prose-a:text-black prose-ol:pl-0 md:max-w-none"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            )}
          </Section>
        </>
      )}
    </>
  );
};

export default withLayout(PrivacyPolicy);

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const client = getApolloClient();

  const location =
    locale == 'uk'
      ? MenuLocationEnum.PRIMARY_NAVIGATION
      : MenuLocationEnum.PRIMARY_NAVIGATION___EN;
  const language = locale?.toUpperCase();
  const newsLanguage = language;
  const { data } = await client.query({
    query: PRIVACY_POLICY_PAGE_QUERY,
    variables: {
      language,
      newsLanguage,
    },
  });

  const {
    data: { menuItems },
  } = await client.query({
    query: ALL_MENU_QUERY,
    variables: {
      location,
    },
  });
  const footerLocation =
    locale == 'uk'
      ? FooterMenuLocationEnum.FOOTER_MENU
      : FooterMenuLocationEnum.FOOTER_MENU___EN;

  const { data: footerMenuItems } =
    locale == 'uk'
      ? await client.query({
          query: FOOTER_DATA_QUERY,
          variables: {
            location: footerLocation,
          },
        })
      : await client.query({
          query: FOOTER_DATA_QUERY_EN,
          variables: {
            location: footerLocation,
          },
        });
  const footerData = footerDataTransformer(footerMenuItems);

  const flatList = flatListToHierarchical(menuItems.nodes);
  const menus = transformMenus(flatList);

  return {
    props: {
      data,
      menus,
      footerData,
      ...(await serverSideTranslations(locale as string, ['common'])),
    },
  };
};
